import { getMatchPercent, formatFitScoreBadge } from "./matchScoreUtils";
import { getSpecValue } from "./productSpecs";

export const MAX_COMPARISON_ROWS = 5;

const CATEGORY_DEFS = {
  match: { compareMode: "max", defaultLabel: "Fit Score" },
  price: { compareMode: "min", defaultLabel: "Price" },
  budget: { compareMode: "budget", defaultLabel: "Vs your budget" },
  value_vs_budget: { compareMode: "budget", defaultLabel: "Value vs Budget" },
  camera_performance: {
    specKeys: ["camera", "megapixel", "rear camera", "main camera"],
    compareMode: "spec_megapixel",
    defaultLabel: "Camera Performance",
    megapixelLabel: "Camera (megapixels)",
  },
  camera: {
    specKeys: ["camera", "megapixel", "rear camera"],
    compareMode: "spec_megapixel",
    defaultLabel: "Camera Performance",
    megapixelLabel: "Camera (megapixels)",
  },
  battery_life: {
    specKeys: ["battery", "battery life"],
    compareMode: "spec_max",
    defaultLabel: "Battery Life",
  },
  battery: {
    specKeys: ["battery", "battery life"],
    compareMode: "spec_max",
    defaultLabel: "Battery Life",
  },
  ram: {
    specKeys: ["ram", "memory"],
    compareMode: "spec_max",
    defaultLabel: "RAM",
  },
  storage: {
    specKeys: ["storage", "capacity"],
    compareMode: "spec_max",
    defaultLabel: "Storage",
  },
  display: {
    specKeys: ["display", "screen"],
    compareMode: "spec_text",
    defaultLabel: "Display",
  },
  processor: {
    specKeys: ["processor", "cpu", "chip"],
    compareMode: "spec_text",
    defaultLabel: "Processor",
  },
  weight: {
    specKeys: ["weight"],
    compareMode: "spec_min",
    defaultLabel: "Weight",
  },
  screen_size: {
    specKeys: ["screen size", "display size", "screen"],
    compareMode: "spec_max",
    defaultLabel: "Screen size",
  },
};

const DEFAULT_ROW_KEYS = [
  "match",
  "camera_performance",
  "price",
  "value_vs_budget",
  "battery_life",
];

/**
 * Some keys are just aliases of each other (same compareMode, same computed
 * value) — e.g. "budget" and "value_vs_budget" both render the exact same
 * "$X under/over" figure. Showing both as separate rows is confusing
 * (duplicate data), so callers should keep only one per group.
 */
const CATEGORY_ALIAS_GROUPS = {
  budget: "budget",
  value_vs_budget: "budget",
  camera_performance: "camera",
  camera: "camera",
  battery_life: "battery",
  battery: "battery",
};

const categoryGroup = (key) => CATEGORY_ALIAS_GROUPS[key] || key;

/** Drop rows that are aliases of an already-selected row, keeping order. */
export const dedupeCategories = (categories = []) => {
  const seenGroups = new Set();
  return categories.filter((cat) => {
    const group = categoryGroup(cat?.key);
    if (!group || seenGroups.has(group)) return false;
    seenGroups.add(group);
    return true;
  });
};

const formatPrice = (product) => {
  const n = Number(product?.price);
  return Number.isFinite(n) ? `$${n.toLocaleString()}` : "—";
};

const formatMatch = (product) => {
  const pct = getMatchPercent(product);
  return pct > 0 ? `${pct}%` : "—";
};

const formatBudgetFit = (product, budgetMax) => {
  const price = Number(product?.price);
  if (!Number.isFinite(budgetMax) || !Number.isFinite(price)) return "—";
  const gap = budgetMax - price;
  if (gap >= 0) return `$${gap.toLocaleString()} under`;
  return `$${Math.abs(gap).toLocaleString()} over`;
};

const parseNumeric = (value) => {
  if (value == null || value === "—") return null;
  const str = String(value).replace(/,/g, "");
  const match = str.match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  let num = parseFloat(match[1]);
  if (/tb/i.test(str)) num *= 1024;
  return Number.isFinite(num) ? num : null;
};

const parseMegapixels = (value) => {
  if (value == null || value === "—") return null;
  const str = String(value).toLowerCase();
  const mpMatch = str.match(/(\d+(?:\.\d+)?)\s*mp/);
  if (mpMatch) return parseFloat(mpMatch[1]);
  const num = parseNumeric(value);
  if (num != null && num >= 12 && num <= 250) return num;
  return null;
};

const findSpecDisplay = (product, specKeys) => {
  for (const key of specKeys) {
    const val = getSpecValue(product, key);
    if (val != null && val !== "") return String(val);
  }
  if (Array.isArray(product?.specifications)) {
    for (const spec of product.specifications) {
      const label = String(spec.label || spec.key || "").toLowerCase();
      if (specKeys.some((k) => label.includes(k)) && spec.value != null) {
        return String(spec.value);
      }
    }
  }
  return "—";
};

const isCameraMegapixelRow = (categoryKey, def) =>
  Boolean(
    def?.compareMode === "spec_megapixel" &&
      (categoryKey === "camera_performance" || categoryKey === "camera"),
  );

/** Use an honest row label when the comparison is megapixel specs, not overall camera quality. */
export const resolveCategoryLabel = (categoryKey, def, values = []) => {
  if (!def) return categoryKey;

  if (isCameraMegapixelRow(categoryKey, def)) {
    const hasMegapixelValues =
      values.length === 0 ||
      values.some((value) => parseMegapixels(value) != null);
    if (hasMegapixelValues) {
      return def.megapixelLabel || "Camera (megapixels)";
    }
  }

  return def.defaultLabel;
};

const findBestIndex = (values, mode) => {
  if (mode === "spec_text") return -1;

  if (mode === "budget") {
    const withSign = values.map((v, i) => {
      if (v === "—") return { i, score: null };
      const isOver = v.includes("over");
      const num = Number(String(v).replace(/[^0-9]/g, ""));
      return { i, score: isOver ? -num : num };
    });
    const valid = withSign.filter((x) => x.score != null);
    if (!valid.length) return -1;
    return valid.reduce((best, cur) =>
      cur.score > best.score ? cur : best,
    ).i;
  }

  if (mode === "spec_megapixel") {
    const nums = values.map((v, i) => ({ i, n: parseMegapixels(v) }));
    const valid = nums.filter((x) => x.n != null);
    if (!valid.length) return -1;
    return valid.reduce((best, cur) => (cur.n > best.n ? cur : best)).i;
  }

  if (mode === "max" || mode === "min" || mode === "spec_max" || mode === "spec_min") {
    const nums = values.map((v, i) => ({
      i,
      n:
        parseNumeric(v) ??
        (Number(String(v).replace(/[^0-9.-]/g, "")) || null),
    }));
    const valid = nums.filter((x) => x.n != null && Number.isFinite(x.n));
    if (!valid.length) return -1;
    if (mode === "min" || mode === "spec_min") {
      return valid.reduce((best, cur) => (cur.n < best.n ? cur : best)).i;
    }
    return valid.reduce((best, cur) => (cur.n > best.n ? cur : best)).i;
  }

  return -1;
};

const extractRowValues = (categoryKey, products, searchParams) => {
  const budgetMax = Number(searchParams?.budget?.max);

  switch (categoryKey) {
    case "match":
      return products.map(formatMatch);
    case "price":
      return products.map(formatPrice);
    case "budget":
    case "value_vs_budget":
      return products.map((p) =>
        Number.isFinite(budgetMax) ? formatBudgetFit(p, budgetMax) : "—",
      );
    default: {
      const def = CATEGORY_DEFS[categoryKey];
      if (!def?.specKeys) return products.map(() => "—");
      return products.map((p) => findSpecDisplay(p, def.specKeys));
    }
  }
};

export const getDefaultCategories = (searchParams = null) => {
  let keys = [...DEFAULT_ROW_KEYS];

  if (searchParams?.budget?.max == null) {
    keys = keys.map((k) => (k === "value_vs_budget" ? "storage" : k));
  }

  const useCase = String(searchParams?.useCase || "").toLowerCase();
  if (useCase.includes("gaming")) {
    keys = keys.map((k) => (k === "camera_performance" ? "processor" : k));
  } else if (useCase.includes("battery") || useCase.includes("travel")) {
    keys = keys.map((k) => (k === "camera_performance" ? "battery_life" : k));
  }

  const seen = new Set();
  return keys
    .filter((k) => CATEGORY_DEFS[k] && !seen.has(k) && seen.add(k))
    .slice(0, MAX_COMPARISON_ROWS)
    .map((key) => ({
      key,
      label: resolveCategoryLabel(key, CATEGORY_DEFS[key], []),
    }));
};

export const buildQuickComparisonTable = ({
  products = [],
  categories = [],
  searchParams = null,
}) => {
  if (!products.length || !categories.length) return null;

  const columns = products.map((product, index) => ({
    id: product._id,
    rank: product.rank ?? index + 1,
    name: product.name,
    isTopPick: index === 0,
  }));

  const rows = categories
    .slice(0, MAX_COMPARISON_ROWS)
    .map((cat) => {
      const def = CATEGORY_DEFS[cat.key];
      if (!def) return null;

      const values = extractRowValues(cat.key, products, searchParams);
      const hasData = values.some((v) => v !== "—");
      const alwaysShow = [
        "match",
        "price",
        "budget",
        "value_vs_budget",
      ].includes(cat.key);

      if (!hasData && !alwaysShow) return null;

      return {
        key: cat.key,
        label: resolveCategoryLabel(cat.key, def, values),
        compareMode: def.compareMode,
        values,
        bestIndex: findBestIndex(values, def.compareMode),
      };
    })
    .filter(Boolean)
    .slice(0, MAX_COMPARISON_ROWS);

  if (!rows.length) return null;

  return { columns, rows };
};

export const MAX_CARD_METRICS = 4;

const valueToScore = (value, compareMode) => {
  if (value == null || value === "—") return null;

  if (compareMode === "max") return parseNumeric(value);
  if (compareMode === "min") {
    const n = parseNumeric(value);
    return n != null ? -n : null;
  }
  if (compareMode === "budget") {
    if (String(value).includes("over")) {
      return -Math.abs(parseNumeric(value) || 0);
    }
    return parseNumeric(value);
  }
  if (compareMode === "spec_megapixel") return parseMegapixels(value);
  if (compareMode === "spec_max") return parseNumeric(value);
  if (compareMode === "spec_min") {
    const n = parseNumeric(value);
    return n != null ? -n : null;
  }
  if (compareMode === "spec_text") return null;
  return parseNumeric(value);
};

/** Minimum visible fill so the lowest value in a row never renders as a
 * blank/invisible bar that looks broken rather than "lowest of the group". */
const MIN_VISIBLE_BAR_PERCENT = 6;

const computeBarPercent = (row, productIndex) => {
  const scores = row.values.map((v) => valueToScore(v, row.compareMode));
  const myScore = scores[productIndex];
  if (myScore == null) return 0;

  // "match" is already a 0–100% score — its bar should reflect that absolute
  // number (e.g. 90% match ≈ 90% full), not a value rescaled relative to the
  // other cards. Rescaling made a 90% match look nearly empty next to a 95%
  // match, which visually contradicts the "90%" label right next to it.
  if (row.key === "match") {
    return Math.max(0, Math.min(100, Math.round(myScore)));
  }

  const valid = scores.filter((s) => s != null);
  if (!valid.length) return 0;

  const min = Math.min(...valid);
  const max = Math.max(...valid);
  if (max === min) return 100;

  const percent = Math.round(((myScore - min) / (max - min)) * 100);
  return Math.max(percent, MIN_VISIBLE_BAR_PERCENT);
};

/** Transform table rows into per-product visual card data. */
export const buildComparisonCards = ({ table, products = [] }) => {
  if (!table?.columns?.length) return [];

  const metricRows = table.rows
    .filter((row) => row.key !== "price")
    .slice(0, MAX_CARD_METRICS);

  const productsById = new Map(
    products.map((p) => [String(p._id), p]),
  );

  return table.columns.map((col, colIndex) => {
    const product = productsById.get(String(col.id)) || products[colIndex];
    const price = formatPrice(product);

    const metrics = metricRows.map((row) => ({
      key: row.key,
      label: row.label,
      value: row.values[colIndex],
      isBest: row.bestIndex === colIndex,
      barPercent: computeBarPercent(row, colIndex),
    }));

    return {
      id: col.id,
      rank: col.rank,
      name: product?.name ?? col.name,
      price,
      metrics,
    };
  });
};

export const getWinnerIndex = (products, winnerProductId) => {
  if (!winnerProductId) return 0;
  const idx = products.findIndex(
    (p) => String(p._id) === String(winnerProductId),
  );
  return idx >= 0 ? idx : 0;
};

/**
 * Ground truth winner: whichever column actually leads on the most rows of
 * the real comparison table (the same rows/checkmarks the user sees), not
 * whatever an LLM claims. Ties break on match %, then lower price.
 * This keeps the "PICK" badge consistent with the visible checkmarks.
 */
export const getDeterministicWinnerIndex = ({ table, products = [] }) => {
  if (!products.length) return -1;
  if (products.length === 1) return 0;

  if (table?.rows?.length && table?.columns?.length) {
    const wins = table.columns.map(() => 0);
    table.rows.forEach((row) => {
      if (row.bestIndex != null && row.bestIndex >= 0) {
        wins[row.bestIndex] = (wins[row.bestIndex] || 0) + 1;
      }
    });

    const maxWins = Math.max(...wins);
    if (maxWins > 0) {
      const contenders = wins
        .map((count, i) => ({ i, count }))
        .filter((c) => c.count === maxWins);
      if (contenders.length === 1) return contenders[0].i;

      return contenders.reduce((best, cur) => {
        const bestMatch = getMatchPercent(products[best.i]);
        const curMatch = getMatchPercent(products[cur.i]);
        if (curMatch !== bestMatch) return curMatch > bestMatch ? cur : best;

        const bestPrice = Number(products[best.i]?.price);
        const curPrice = Number(products[cur.i]?.price);
        if (Number.isFinite(curPrice) && Number.isFinite(bestPrice) && curPrice !== bestPrice) {
          return curPrice < bestPrice ? cur : best;
        }
        return best;
      }, contenders[0]).i;
    }
  }

  return products.reduce((bestIdx, product, i) => {
    if (bestIdx === -1) return i;
    return getMatchPercent(product) > getMatchPercent(products[bestIdx]) ? i : bestIdx;
  }, -1);
};

/** Rows that define "best value" — price and budget fit, not overall match score. */
const VALUE_PICK_ROW_KEYS = new Set(["price", "value_vs_budget", "budget"]);

/**
 * Best-value pick: leads on price / budget-fit rows only, so a cheaper phone
 * can be highlighted even when another product wins on match % and specs.
 */
export const getValuePickIndex = ({ table, products = [] }) => {
  if (!products.length) return -1;
  if (products.length === 1) return 0;

  const valueRows =
    table?.rows?.filter((row) => VALUE_PICK_ROW_KEYS.has(row.key)) || [];

  if (valueRows.length > 0 && table?.columns?.length) {
    const wins = table.columns.map(() => 0);
    valueRows.forEach((row) => {
      if (row.bestIndex != null && row.bestIndex >= 0) {
        wins[row.bestIndex] = (wins[row.bestIndex] || 0) + 1;
      }
    });

    const maxWins = Math.max(...wins);
    if (maxWins > 0) {
      const contenders = wins
        .map((count, i) => ({ i, count }))
        .filter((c) => c.count === maxWins);

      if (contenders.length === 1) return contenders[0].i;

      return contenders.reduce((best, cur) => {
        const bestPrice = Number(products[best.i]?.price);
        const curPrice = Number(products[cur.i]?.price);
        if (
          Number.isFinite(curPrice) &&
          Number.isFinite(bestPrice) &&
          curPrice !== bestPrice
        ) {
          return curPrice < bestPrice ? cur : best;
        }
        return best;
      }, contenders[0]).i;
    }
  }

  return products.reduce((bestIdx, product, i) => {
    const price = Number(product?.price);
    if (!Number.isFinite(price)) return bestIdx;
    const bestPrice = Number(products[bestIdx]?.price);
    if (!Number.isFinite(bestPrice) || price < bestPrice) return i;
    return bestIdx;
  }, 0);
};

export const getTopMatchIndex = (products = []) => {
  if (!products.length) return -1;
  if (products.length === 1) return 0;

  const hasRank = products.some(
    (product) => product.rank != null && Number.isFinite(Number(product.rank)),
  );

  if (hasRank) {
    const rankOneIndex = products.findIndex(
      (product) => Number(product.rank) === 1,
    );
    if (rankOneIndex >= 0) return rankOneIndex;

    return products.reduce((bestIndex, product, index) => {
      const bestRank = Number(products[bestIndex].rank ?? 99);
      const currentRank = Number(product.rank ?? 99);
      return currentRank < bestRank ? index : bestIndex;
    }, 0);
  }

  return products.reduce((bestIndex, product, index) => {
    return getMatchPercent(product) > getMatchPercent(products[bestIndex])
      ? index
      : bestIndex;
  }, 0);
};

export const getComparisonPicks = (products = [], searchParams = null) => {
  const topMatchIndex = getTopMatchIndex(products);
  const table = buildQuickComparisonTable({
    products,
    categories: getDefaultCategories(searchParams),
    searchParams,
  });
  const valuePickIndex = getValuePickIndex({ table, products });
  const resolvedValuePickIndex = valuePickIndex >= 0 ? valuePickIndex : topMatchIndex;

  return {
    topMatchIndex,
    valuePickIndex: resolvedValuePickIndex,
    isValuePick:
      topMatchIndex >= 0 &&
      resolvedValuePickIndex >= 0 &&
      topMatchIndex !== resolvedValuePickIndex,
  };
};

export const buildFallbackRecommendation = (
  products,
  searchParams = null,
  winnerIndex = 0,
) => {
  if (!products.length) return null;
  const winner = products[winnerIndex] || products[0];
  const topMatchIndex = getTopMatchIndex(products);
  const topMatch = products[topMatchIndex] || winner;
  const isValuePick = topMatchIndex >= 0 && winnerIndex !== topMatchIndex;
  const useCase = searchParams?.useCase;
  const match = getMatchPercent(winner);
  const price = formatPrice(winner);
  const topMatchScore = getMatchPercent(topMatch);

  let summary;
  if (isValuePick) {
    const useCasePhrase = useCase ? ` for ${useCase.toLowerCase()}` : "";
    summary = `At ${price}, the ${winner.name} stretches your budget furthest${useCasePhrase} while still meeting your must-haves. ${topMatch.name} scores higher (${formatFitScoreBadge(topMatchScore)}), but costs more.`;
  } else if (useCase && match > 0) {
    summary = `${winner.name} is the best fit for your ${useCase.toLowerCase()} needs with a ${formatFitScoreBadge(match)}.`;
  } else if (match > 0) {
    summary = `${winner.name} leads with a ${formatFitScoreBadge(match)} — the strongest overall fit among these options.`;
  } else {
    summary = `${winner.name} ranks #1 for your search — the strongest overall fit among these options.`;
  }

  return {
    winnerProductId: winner._id,
    topMatchProductId: topMatch._id,
    isValuePick,
    summary,
  };
};
